import { ShoppingCart, ScanLine, Minus, Plus, Trash2, Banknote, CreditCard, CircleDollarSign, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// Venta Rápida · Retail (ventana emergente) — presentacional.
export const VentaRapidaDialog = ({
  retailModalOpen, cerrarVentaRapida, retailInputRef, retailQuery, setRetailQuery, retailResolveScan,
  catData, retailProductos, retailAdd, caja,
  retailCart, retailCartCount, retailCartTotal, retailClear, retailSetQty, retailRemove,
  retailMetodo, setRetailMetodo, retailPagar, retailPaying,
}) => (
  <Dialog open={retailModalOpen} onOpenChange={cerrarVentaRapida}>
    <DialogContent className="rounded-none border-white/10 bg-[#0A0A0A] text-white max-w-lg max-h-[92dvh] overflow-y-auto overflow-x-hidden" data-testid="retail-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 font-display text-xl font-black tracking-tight"><ShoppingCart className="h-5 w-5 text-[#FFCC00]" /> Venta Rápida · Retail</DialogTitle>
        <DialogDescription className="text-white/50">Escanea o busca productos, ajusta cantidades y cobra en una sola transacción.</DialogDescription>
      </DialogHeader>
      <div className="min-w-0">
        <div className="relative mb-3">
          <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#FFCC00]" />
          <Input ref={retailInputRef} value={retailQuery} onChange={(e) => setRetailQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); retailResolveScan(); } }}
            placeholder="Escanear o buscar por nombre / código" data-testid="retail-search"
            className="h-11 pl-10 rounded-none bg-white/5 border-white/20 text-white focus-visible:ring-2 focus-visible:ring-[#FFCC00]" />
        </div>
        {catData.productos.length === 0 ? (
          <p className="text-white/30 text-xs py-8 text-center border border-white/10" data-testid="retail-empty">No hay productos configurados en esta sucursal.</p>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5 max-h-48 overflow-y-auto" data-testid="retail-list">
            {retailProductos.map((p) => (
              <button key={p.id_producto} onClick={() => retailAdd(p)} disabled={!caja}
                data-testid={`retail-prod-${p.id_producto}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-[#FFCC00]/5 disabled:opacity-40 disabled:cursor-not-allowed text-left transition-colors duration-150">
                <div className="min-w-0">
                  <span className="block font-display font-bold text-sm text-white/90 truncate">{p.nombre || p.sku_interno}</span>
                  <span className="text-[10px] uppercase tracking-wider text-white/40 truncate block">{p.codigo_barras || p.sku_interno}{p.control_inventario ? ` · ${p.stock} en stock` : ""}</span>
                </div>
                <span className="font-mono font-bold text-[#FFCC00] shrink-0">${Number(p.precio).toFixed(2)}</span>
              </button>
            ))}
            {retailProductos.length === 0 && (
              <p className="p-4 text-center text-xs text-white/40" data-testid="retail-no-results">Sin resultados para "{retailQuery}".</p>
            )}
          </div>
        )}

        {/* Carrito */}
        <div className="mt-4" data-testid="retail-cart">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/50 flex items-center gap-1.5"><ShoppingCart className="h-3.5 w-3.5 text-[#FFCC00]" /> Carrito ({retailCartCount})</span>
            {retailCart.length > 0 && (
              <button onClick={retailClear} data-testid="retail-clear-btn"
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/40 hover:text-[#EF4444] transition-colors">
                <Trash2 className="h-3 w-3" /> Vaciar
              </button>
            )}
          </div>
          {retailCart.length === 0 ? (
            <p className="text-white/30 text-xs py-5 text-center border border-white/10" data-testid="retail-cart-empty">Escanea o toca un producto para agregarlo.</p>
          ) : (
            <div className="border border-white/10 divide-y divide-white/5" data-testid="retail-cart-items">
              {retailCart.map((i) => (
                <div key={i.id_producto} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`retail-item-${i.id_producto}`}>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate">{i.nombre}</span>
                    <span className="font-mono text-white/50 text-xs">${Number(i.precio).toFixed(2)} c/u</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => retailSetQty(i.id_producto, -1)} data-testid={`retail-minus-${i.id_producto}`} className="h-7 w-7 flex items-center justify-center border border-white/20 hover:bg-white/10"><Minus className="h-3 w-3" /></button>
                    <span className="w-6 text-center font-mono" data-testid={`retail-qty-${i.id_producto}`}>{i.cantidad}</span>
                    <button onClick={() => retailSetQty(i.id_producto, 1)} data-testid={`retail-plus-${i.id_producto}`} className="h-7 w-7 flex items-center justify-center border border-white/20 hover:bg-white/10"><Plus className="h-3 w-3" /></button>
                    <button onClick={() => retailRemove(i.id_producto)} data-testid={`retail-del-${i.id_producto}`} className="h-7 w-7 flex items-center justify-center border border-white/20 hover:border-[#EF4444] hover:text-[#EF4444]"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {retailCart.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="retail-checkout">
            <div className="flex justify-between items-baseline px-1">
              <span className="text-xs uppercase tracking-wider font-bold text-white/60">Total</span>
              <span className="font-display text-3xl font-black text-[#FFCC00]" data-testid="retail-total">${retailCartTotal.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setRetailMetodo("Efectivo")} data-testid="retail-metodo-efectivo"
                className={`btn-hard h-11 border flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-xs ${retailMetodo === "Efectivo" ? "bg-[#10B981]/15 border-[#10B981] text-[#10B981]" : "bg-white/5 border-white/20 text-white/70"}`}>
                <Banknote className="h-4 w-4" /> Efectivo
              </button>
              <button onClick={() => setRetailMetodo("Tarjeta")} data-testid="retail-metodo-tarjeta"
                className={`btn-hard h-11 border flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-xs ${retailMetodo === "Tarjeta" ? "bg-[#3B82F6]/15 border-[#3B82F6] text-[#3B82F6]" : "bg-white/5 border-white/20 text-white/70"}`}>
                <CreditCard className="h-4 w-4" /> Tarjeta
              </button>
            </div>
            <button onClick={retailPagar} disabled={retailPaying || !caja} data-testid="retail-pagar-btn"
              className="btn-hard w-full h-14 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-black uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
              {retailPaying ? <Loader2 className="h-5 w-5 animate-spin" /> : <CircleDollarSign className="h-5 w-5" />} Pagar ${retailCartTotal.toFixed(2)}
            </button>
          </div>
        )}
        {!caja && <p className="mt-2 text-[11px] text-white/40" data-testid="retail-modal-sin-caja">Abre un turno para cobrar productos.</p>}
      </div>
    </DialogContent>
  </Dialog>
);

export default VentaRapidaDialog;
